import {
  CREATE_STRIPE_TOKEN,
  CREATE_BILLING_PROFILE,
  CREATE_PAID_SUBSCRIPTION,
  CREATE_USER_COUPON,
  ADD_CARD,
  FETCH_BILLING_PROFILES,
  FETCH_PAID_SUBSCRIPTIONS,
  DISABLE_AUTO_RENEW,
  FETCH_PRODUCTS,
  FETCH_COUPON,
  FETCH_PRODUCT,
  APPLY_COUPON,
  SELECT_PRODUCT,
  UPDATE_AUTO_RENEW,
  DELETE_BILLING_PROFILE,
  UPDATE_DEFAULT_BILLING_PROFILE,
  UPDATE_DEFAULT_BILLING_PROFILE_LOADING,
  CHECKOUT,
  CHECKOUT_PAGE,
  SUBSCRIPTION_SETTINGS,
  ADD_BILLING_PROFILE,
  FETCH_USER_COUPONS,
  REDEEM_COUPON,
  UPDATE_BILLING_PROFILE,
} from './constants'
import { selectBillingProfiles } from './selectors'

export function chooseProduct (product) {
  return {
    type: SELECT_PRODUCT,
    payload: product,
  }
}

export function fetchCoupon (couponCode) {
  return (dispatch, getState, { premium }) => {
    return dispatch({
      type: FETCH_COUPON,
      payload: premium.getCoupon(couponCode),
    })
  }
}

export function fetchProduct (productId) {
  return (dispatch, getState, { premium }) => {
    return dispatch({
      type: FETCH_PRODUCT,
      payload: premium.getProduct(productId),
    })
  }
}

export function fetchProducts (productIds = []) {
  return (dispatch, getState, { premium }) => {
    const products = productIds.map((id) => premium.getProduct(id))
    return dispatch({
      type: FETCH_PRODUCTS,
      payload: Promise.all(products),
    })
  }
}

export function fetchBillingProfiles () {
  return (dispatch, getState, { premium }) => {
    return dispatch({
      type: FETCH_BILLING_PROFILES,
      payload: premium.getBillingProfiles(),
    })
  }
}

export function fetchUserCoupons () {
  return (dispatch, getState, { premium }) => {
    return dispatch({
      type: FETCH_USER_COUPONS,
      payload: premium.getUserCoupons(),
    })
  }
}

export function createUserCoupon ({ couponCode, userId, countryCode }) {
  return (dispatch, getState, { premium }) => {
    return dispatch({
      type: CREATE_USER_COUPON,
      payload: premium.postUserCoupon({ couponCode, userId, countryCode }),
    })
  }
}

export function applyCoupon ({ couponCode, userId, countryCode }) {
  return (dispatch) => {
    const promise = Promise.resolve()
      // fetch user coupons
      .then(() => {
        return dispatch(fetchUserCoupons())
      })
      // determine if we need to post user coupon, if it's already
      // been done, or if it's already been redeemed
      .then(({ value }) => {
        const { data: { items: coupons }} = value
        const existingUserCoupon = coupons.find((coupon) => {
          return coupon.coupon_id === couponCode
        })

        if (existingUserCoupon && !existingUserCoupon.redeemed) {
          // probably user attempted, but didn't finish checkout w/ code
          return true
        } else if (existingUserCoupon && existingUserCoupon.redeemed) {
          // coupon was successfully redeemed/checked-out previously
          return Promise.reject()
        } else {
          // first time using coupon for this user
          return dispatch(createUserCoupon({ couponCode, userId, countryCode }))
        }

      })
      .then(() => {
        return dispatch(fetchCoupon(couponCode))
      })
      .then(({ value: res }) => {
        const { data: { item: coupon }} = res
        return dispatch(fetchProduct(coupon.product_id))
      })
      .then(({ value: response }) => {
        const { data: { item: product }} = response
        return dispatch(chooseProduct(product))
      })

    return dispatch({
      type: APPLY_COUPON,
      payload: {
        promise,
        data: couponCode,
      },
    })
  }
}

export function createStripeToken (card) {
  return (dispatch, getState, { premium }) => {
    return dispatch({
      type: CREATE_STRIPE_TOKEN,
      payload: premium.getStripeToken(card),
    })
  }
}

export function fetchPaidSubscriptions () {
  return (dispatch, getState, { premium }) => {
    return dispatch({
      type: FETCH_PAID_SUBSCRIPTIONS,
      payload: premium.getPaidSubscriptions(),
    })
  }
}

export function loadCheckoutPage ({ code, userId, chooserProducts }) {
  return (dispatch, getState) => {
    const promise = Promise.resolve()
      .then(() => {
        return dispatch(fetchBillingProfiles())
      })
      .then(() => {
        return dispatch(fetchPaidSubscriptions())
      })
      .then(() => {
        const { premium: { userCoupon, selectedProduct }} = getState()
        const hasValidatedCoupon = userCoupon && selectedProduct

        if (hasValidatedCoupon) {
          return { hasValidatedCoupon }
        } else if (code) {
          return dispatch(applyCoupon({ couponCode: code, userId }))
        }

        return dispatch(fetchProducts(chooserProducts))
      })

    return dispatch({
      type: CHECKOUT_PAGE,
      payload: promise,
    })
  }
}

export function loadSubscriptionSettings () {
  return (dispatch) => {
    const promise = Promise.resolve()
      .then(() => Promise.all([
        dispatch(fetchPaidSubscriptions()),
        dispatch(fetchBillingProfiles()),
      ]))

    return dispatch({
      type: SUBSCRIPTION_SETTINGS,
      payload: promise,
    })
  }
}

export function updateAutoRenewal ({ subscriptionId, autoRenewal }) {
  return (dispatch, getState, { premium }) => {
    return dispatch({
      type: UPDATE_AUTO_RENEW,
      payload: {
        promise: premium.updateAutoRenewal({ subscriptionId, autoRenewal }),
        data: { subscriptionId, autoRenewal },
      },
    })
  }
}

export function disableAutoRenewal (subscriptionId) {
  return (dispatch, getState, { premium }) => {
    return dispatch({
      type: DISABLE_AUTO_RENEW,
      payload: premium.disableAutoRenewal(subscriptionId),
    })
  }
}

export function createBillingProfile ({ token, provider }) {
  return (dispatch, getState, { premium }) => {
    return dispatch({
      type: CREATE_BILLING_PROFILE,
      payload: premium.postBillingProfile({ token, provider }),
    })
  }
}

export function updateDefaultBillingProfile (id, persist = true) {
  if (!persist) {
    return { type: UPDATE_DEFAULT_BILLING_PROFILE_LOADING, payload: id }
  }

  return (dispatch, getState, { premium }) => {
    const billingProfiles = selectBillingProfiles(getState())
    const profile = billingProfiles.find((p) => p.billing_profile_id === id)
    const newProfile = { ...profile, default: true }

    return dispatch({
      type: UPDATE_DEFAULT_BILLING_PROFILE,
      payload: {
        promise: premium.patchDefaultBillingProfile({ id, profile: newProfile }),
        data: id,
      },
    })
  }
}

export function updateBillingProfile (id, card) {
  return (dispatch, getState, { premium }) => {
    const billingProfiles = selectBillingProfiles(getState())
    const profile = billingProfiles.find((p) => p.billing_profile_id === id)
    const newProfile = {
      ...profile,
      default: true,
      billing_zip_code: card.address_zip,
      cc_expiration_month: parseInt(card.exp_month, 10),
      cc_expiration_year: parseInt(card.exp_year, 10),
      customer_name: card.name,
    }

    return dispatch({
      type: UPDATE_BILLING_PROFILE,
      payload: {
        promise: premium.patchBillingProfile({ id, data: { item: newProfile }}),
        data: newProfile,
      },
    })
  }
}

export function addCard (card) {
  return (dispatch) => {
    const promise = Promise.resolve()
      .then(() => {
        return dispatch(createStripeToken(card))
      })
      .then(({ value: token }) => {
        return dispatch(createBillingProfile({ token, provider: 'stripe' }))
      })

    return dispatch({
      type: ADD_CARD,
      payload: promise,
    })
  }
}

export function addBillingProfile (card) {
  return (dispatch) => {
    return dispatch({
      type: ADD_BILLING_PROFILE,
      payload: dispatch(addCard(card)),
    })
  }
}

export function createPaidSubscription ({ product, coupon, provider, countryCode }) {
  return (dispatch, getState, { premium }) => {
    return dispatch({
      type: CREATE_PAID_SUBSCRIPTION,
      payload: premium.postPaidSubscription({ product, coupon, provider, countryCode }),
    })
  }
}

export function redeemCoupon (coupon) {
  return (dispatch, getState, { premium }) => {
    return dispatch({
      type: REDEEM_COUPON,
      payload: premium.redeemCoupon(coupon),
    })
  }
}

export function checkout ({ product, coupon, provider, card, billingProfileId, countryCode }) {
  return (dispatch) => {
    const promise = Promise.resolve()
      .then(() => {
        if (card) {
          return dispatch(addCard(card))
            .then((res) => res.action.payload.value.data.item.billing_profile_id)
        }
      })
      .then((newBillingProfileId) => {
        const billingId = billingProfileId || newBillingProfileId
        if (billingId) { return dispatch(updateDefaultBillingProfile(billingId)) }
      })
      .then(() => {
        if (coupon) {
          return dispatch(redeemCoupon(coupon))
        }
        return dispatch(createPaidSubscription({ product, provider, countryCode }))
      })
      .then(() => {
        return dispatch(fetchPaidSubscriptions())
      })

    return dispatch({
      type: CHECKOUT,
      payload: promise,
    })
  }
}

export function deleteBillingProfile (id) {
  return (dispatch, getState, { premium }) => {
    return dispatch({
      type: DELETE_BILLING_PROFILE,
      payload: {
        promise: premium.deleteBillingProfile(id).then(() => id),
        data: id,
      },
    })
  }
}



// WEBPACK FOOTER //
// ./src/services/premium/actions.js